/**
 * Scenario API functions.
 *
 * Maps to backend scenario-service endpoints:
 *   GET    /api/scenarios                 - List scenarios (filterable by community_id, status)
 *   POST   /api/scenarios                 - Create scenario
 *   GET    /api/scenarios/:id             - Get scenario detail
 *   PUT    /api/scenarios/:id             - Update scenario (version increment)
 *   DELETE /api/scenarios/:id             - Delete/archive scenario
 *   POST   /api/scenarios/:id/fork        - Fork scenario for what-if
 */

import client from './client';

/**
 * List scenarios with optional filters.
 * @param {Object} [params] - { community_id?, status?, skip?, limit? }
 * @returns {Promise<Array>} Array of scenario objects
 */
export const getScenarios = async (params = {}) => {
  return client.get('/api/scenarios', { params });
};

/**
 * Get a single scenario by ID (includes bindings).
 * @param {string} scenarioId - UUID
 * @returns {Promise<Object>} Scenario detail object
 */
export const getScenario = async (scenarioId) => {
  return client.get(`/api/scenarios/${scenarioId}`);
};

/**
 * Create a new scenario.
 * @param {Object} data - {
 *   community_id, name, horizon_start, horizon_days?, scope?, objective?,
 *   policies?, finance_assumptions?, metadata?
 * }
 * @returns {Promise<Object>} Created scenario
 */
export const createScenario = async (data) => {
  return client.post('/api/scenarios', data);
};

/**
 * Update a scenario (auto-increments version on backend).
 * @param {string} scenarioId - UUID
 * @param {Object} data - Fields to update
 * @returns {Promise<Object>} Updated scenario
 */
export const updateScenario = async (scenarioId, data) => {
  return client.put(`/api/scenarios/${scenarioId}`, data);
};

/**
 * Delete or archive a scenario.
 * @param {string} scenarioId - UUID
 * @param {boolean} [archiveOnly=false] - If true, soft-delete (status=archived)
 * @returns {Promise<void>}
 */
export const deleteScenario = async (scenarioId, archiveOnly = false) => {
  return client.delete(`/api/scenarios/${scenarioId}`, {
    params: { archive_only: archiveOnly },
  });
};

/**
 * Fork (clone) a scenario for what-if analysis.
 * @param {string} scenarioId - UUID of the source scenario
 * @param {Object} data - { new_name }
 * @returns {Promise<Object>} Forked scenario (version=1)
 */
export const forkScenario = async (scenarioId, data) => {
  return client.post(`/api/scenarios/${scenarioId}/fork`, data);
};
